import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { LanguageContext } from '../LanguageContext';
import OfferCard from '../components/OfferCard';
import imgHajj2025 from '../img/tour-thumb06.jpg';
import Haj_2025 from '../img/Makkah Picture.jpg';

function Hajj2025() {
  const { language } = useContext(LanguageContext);

  const content = {
    fr: {
      pageTitle: "NOTRE HAJJ",
      description1: "Le Hajj est le cinquième pilier de l'islam, un pèlerinage annuel à La Mecque que tout musulman adulte doit accomplir au moins une fois dans sa vie, s'il en a les moyens physiques et financiers.",
      description2: "Il se déroule du 8 au 13 du mois de Dhu al-Hijjah du calendrier islamique.",
      description3: "Nos offres pour le Hajj 2026 incluent une gamme complète de services pour vous assurer un pèlerinage serein et spirituellement enrichissant.",
      offersTitle: "Nos Offres Hajj 2026",
      soonTag: "PROCHAINEMENT"
    },
    ar: {
      pageTitle: "حجنا",
      description1: "الحج هو الركن الخامس من الإسلام، وهو رحلة سنوية إلى مكة المكرمة يجب على كل مسلم بالغ القيام بها مرة واحدة على الأقل في حياته إذا كان لديه الوسائل المادية والمالية.",
      description2: "يتم أداء الحج من 8 إلى 13 من شهر ذي الحجة حسب التقويم الهجري.",
      description3: "تشمل عروضنا لحج 2026 مجموعة كاملة من الخدمات لضمان حج هادئ ومثمر روحياً.",
      offersTitle: "عروضنا لحج 2026",
      soonTag: "قريبًا"
    }
  };

  const textAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
  };

  const cardAnimation = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      y: -5,
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
    }
  };

  const offersData = [
    {
      thumbImage: imgHajj2025,
      title: language === 'fr' ? 'Hajj 2026' : 'حج 2026',
      dates: language === 'fr' ? 'Du 8 au 13 Dhul Hijjah 1447' : 'من 8 إلى 13 ذو الحجة 1446',
      airline: 'Saudi Airlines',
      duration: language === 'fr' ? '14 Jours' : '14 يومًا',
      price: language === 'fr' ? '' : '',
      link: '#',
      tag: content[language].soonTag,
      soon: true,
    },
  ];

  return (
    <section className="bg-gradient-to-r from-blue-50 via-green-100 to-blue-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="relative h-[500px] overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 h-full"
        >
          <img
            className="w-full h-full object-cover"
            src={Haj_2025}
            alt={language === 'fr' ? "Hajj Background" : "خلفية الحج"}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80"></div>
        </motion.div>
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={textAnimation}
          className="relative h-full flex flex-col items-center justify-center px-4 text-center"
        >
          <h2 className="text-white text-5xl md:text-6xl font-extrabold mb-4 tracking-wide">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-300 to-green-500">
              {content[language].pageTitle}
            </span>
          </h2>
          <div className="w-32 h-2 bg-green-400 rounded-full mb-6"></div>
          <p className="text-gray-200 text-xl max-w-2xl">
            {language === 'fr' 
              ? "Le voyage spirituel d'une vie"
              : "رحلة العمر الروحية"}
          </p>
        </motion.div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={textAnimation}
          className="mb-12 text-center max-w-4xl mx-auto"
        >
          <p className="text-lg text-gray-700 mb-4 leading-relaxed">
            {content[language].description1}
          </p>
          <p className="text-lg text-gray-700 mb-4 leading-relaxed">
            {content[language].description2}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {content[language].description3}
          </p>
        </motion.div>

        <motion.h3
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={textAnimation}
          className="text-4xl font-bold text-center mb-12 relative"
        >
          <span className="relative z-10 px-4 bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
            {content[language].offersTitle}
          </span>
          <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-green-400 to-blue-400 rounded-full"></span>
        </motion.h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {offersData.map((offer, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              variants={cardAnimation}
              className="h-full"
            >
              <div className="h-full">
                <OfferCard 
                  offer={offer} 
                  language={language}
                  className="h-full transform transition-all duration-300 hover:z-10"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Hajj2025;